import React from 'react';
import { useIdleTimerContext } from 'react-idle-timer';
import { useObserver } from 'mobx-react-lite';
import styled from 'styled-components';
import Backdrop from '@material-ui/core/Backdrop';

import useCart from 'src/hooks/use-cart';
import useUI from 'src/hooks/use-ui';
import useStores from 'shared/hooks/use-stores';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';

import { ModalWrapper } from 'shared/modals/v2-wrapper';
import { DesktopOnly, MobileOnly } from 'shared/components';
import { removePersistedValue } from 'shared/utils/persisted-values';

import { ModalContainer, BottomDrawer } from 'src/components/core';
import { withModalControlsHook, ModalComponentProps } from 'src/components/modals/with-modal-controls-hook';
import { KioskProIdleWarning } from 'src/components/modals/kiosk-pro-idle-warning';
import { IdleWarning } from './idle-warning';

const BlurBackdropComponent = styled(Backdrop)`
  &.MuiBackdrop-root {
    backdrop-filter: blur(12px) !important;
    -webkit-backdrop-filter: blur(12px) !important;
  }
`;

const IdleWarningModal = ({ onClose }: ModalComponentProps): JSX.Element => {
  const { clearOrder } = useCart();
  const UI = useUI();
  const { User } = useStores();
  const { dispensary } = useDispensary();
  const idleTimer = useIdleTimerContext();
  const dispoCname = dispensary?.cName;
  const isKioskProEnabled = useKioskPro();
  const isUserLoggedIn = useObserver(() => User?.isLoggedIn);

  const closeAndContinue = (): void => {
    onClose();
    idleTimer.reset();
  };

  const startNewSession = (): void => {
    UI.showKioskCheckoutModal = false;
    UI.setIsKioskProLoginModalOpen(false);
    UI.closeCartDrawer();
    UI.closeCartInterstitial();
    clearOrder();
    onClose();
    idleTimer.reset();
    if (isUserLoggedIn) {
      User?.logout();
    }
    UI.redirectToKioskLockScreen(dispoCname);
  };

  // Clear the launch darkly client id to ensure that kiosk
  // devices aren't treated as a single user across sessions
  removePersistedValue('launch-darkly-client-id');

  return (
    <>
      <DesktopOnly mediaQuery='sm'>
        <ModalWrapper
          open
          onClose={closeAndContinue}
          {...(isKioskProEnabled ? { BackdropComponent: BlurBackdropComponent } : {})}
        >
          <ModalContainer
            width={isKioskProEnabled ? '100%' : '470px'}
            maxWidth={isKioskProEnabled ? '820px' : '470px'}
            p='32px'
          >
            {isKioskProEnabled ? (
              <KioskProIdleWarning closeAndContinue={closeAndContinue} startNewSession={startNewSession} />
            ) : (
              <IdleWarning closeAndContinue={closeAndContinue} startNewSession={startNewSession} />
            )}
          </ModalContainer>
        </ModalWrapper>
      </DesktopOnly>
      <MobileOnly mediaQuery='xs'>
        <BottomDrawer open onClose={closeAndContinue}>
          {isKioskProEnabled ? (
            <KioskProIdleWarning closeAndContinue={closeAndContinue} startNewSession={startNewSession} />
          ) : (
            <IdleWarning closeAndContinue={closeAndContinue} startNewSession={startNewSession} />
          )}
        </BottomDrawer>
      </MobileOnly>
    </>
  );
};

const [IdleWarningController, useIdleWarningModal] = withModalControlsHook(IdleWarningModal);

export { useIdleWarningModal, IdleWarningController };
